import React, { useMemo } from 'react';
import { connect } from 'react-redux';
import { Container } from 'semantic-ui-react';
import StatCard from './StatCard';
import { parseDate } from '../helpers/DateUtils';
import { hasResultsAndActivated } from '../helpers/EventUtils';
import './Landing.css';

const HeroSection = ({ events }) => {
  // Calculate dynamic stats using useMemo for performance
  const stats = useMemo(() => {
    if (!events || events.length === 0) {
      return {
        thisWeekCount: 0,
        thisMonthCount: 0,
        nextGameDate: 'TBD',
      };
    }

    const now = new Date();
    const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // This Week: Count events from last 7 days with results
    const thisWeekCount = events.filter((event) => {
      try {
        const eventDate = parseDate(event.event_year, event.event_month, event.event_day);
        return (
          eventDate >= oneWeekAgo &&
          eventDate <= now &&
          hasResultsAndActivated(event)
        );
      } catch (e) {
        return false;
      }
    }).length;

    // This Month: Count games in current calendar month with results
    const thisMonthCount = events.filter((event) => {
      try {
        const eventDate = parseDate(event.event_year, event.event_month, event.event_day);
        return (
          eventDate.getMonth() === currentMonth &&
          eventDate.getFullYear() === currentYear &&
          hasResultsAndActivated(event)
        );
      } catch (e) {
        return false;
      }
    }).length;

    // Next Game: Find earliest upcoming game date
    const upcomingGames = events
      .filter((event) => {
        try {
          const eventDate = parseDate(event.event_year, event.event_month, event.event_day);
          return eventDate > now;
        } catch (e) {
          return false;
        }
      })
      .sort((a, b) => {
        const dateA = parseDate(a.event_year, a.event_month, a.event_day);
        const dateB = parseDate(b.event_year, b.event_month, b.event_day);
        return dateA - dateB;
      });

    const nextGameDate =
      upcomingGames.length > 0
        ? `${upcomingGames[0].event_month.substring(0, 3)} ${upcomingGames[0].event_day}`
        : 'TBD';

    return { thisWeekCount, thisMonthCount, nextGameDate };
  }, [events]);

  // Format display values with better placeholders
  const thisWeekDisplay = stats.thisWeekCount > 0
    ? `${stats.thisWeekCount} ${stats.thisWeekCount === 1 ? 'game' : 'games'}`
    : 'No games yet';

  const thisMonthDisplay = stats.thisMonthCount > 0
    ? `${stats.thisMonthCount} ${stats.thisMonthCount === 1 ? 'game' : 'games'}`
    : 'No games yet';

  const nextGameDisplay = stats.nextGameDate !== 'TBD'
    ? stats.nextGameDate
    : 'To be announced';

  return (
    <div className="hero-section">
      <Container>
        <h1 className="hero-title">Mayo Bridge Club Results</h1>
        <p className="hero-subtitle">
          Stay up-to-date with the latest results from bridge clubs across Mayo.
        </p>

        <div className="stat-cards-container">
          <StatCard icon="trophy" label="This Week" value={thisWeekDisplay} />
          <StatCard icon="calendar" label="This Month" value={thisMonthDisplay} />
          <StatCard icon="clock" label="Next Game" value={nextGameDisplay} />
        </div>
      </Container>
    </div>
  );
};

const mapStateToProps = (state) => ({
  events: state.events,
});

export default connect(mapStateToProps)(HeroSection);
